import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { PageEvent } from '@angular/material/paginator';
import { SpinnerService } from 'src/app/shared/services/spinner.service';
import { VesTransactionsService } from '../../services/ves-transactions.service';
import { WorkingAccountService } from '../../services/working-account.service';
import { CuentaTrabajo } from '../../interfaces/cuenta-trabajo.interface';
import { ApproveReferenciaModalComponent } from './approve-referencia-modal/approve-referencia-modal.component';
import { RejectReferenciaModalComponent } from './reject-referencia-modal/reject-referencia-modal.component';
import { RegistrarReferenciaOperadorModalComponent } from './registrar-referencia-operador-modal/registrar-referencia-operador-modal.component';
import { EmparejarN58ModalComponent } from './emparejar-n58-modal/emparejar-n58-modal.component';

interface ReferenciaVesAdmin {
  id: string;
  userId: string;
  banco: string;
  cuenta: string;
  tipoCuenta: string;
  titular: string;
  referencia: string;
  monto: number;
  status: string;
  motivoRechazo: string | null;
  createdAt: string;
  updatedAt: string;
  cuentaTrabajoMatch?: CuentaTrabajo | null;
  usuario?: {
    nombre: string;
    apellidos: string;
    email: string;
    [key: string]: any;
  } | null;
  /** Mail del operador que registró la carga. Su única identidad: no tiene
   *  fila en `usuarios`, existe sólo en Cognito. */
  operatorEmail?: string | null;
  /**
   * `tipo_cuenta === 'Interna'`: la registró un operador, no un cliente. El
   * backend manda `usuario` en null en esas filas a propósito — el `user_id`
   * que tienen no es de quien cargó.
   */
  esOperador?: boolean;
}

@Component({
  selector: 'app-referencias-ves-admin',
  templateUrl: './referencias-ves-admin.component.html',
  styleUrls: ['./referencias-ves-admin.component.sass'],
})
export class ReferenciasVesAdminComponent implements OnInit, OnDestroy {
  referencias: ReferenciaVesAdmin[] = [];
  cuentasTrabajoVe: CuentaTrabajo[] = [];
  page = 1;
  limit = 20;
  total = 0;
  totalPages = 0;
  loading = false;
  expandedCardId: string | null = null;

  activeTab: 'cliente' | 'operador' = 'cliente';
  activeRail: 'ALL' | 'N58' | 'BANCO_PLAZA' | 'R4' = 'ALL';

  filterForm: FormGroup;
  pageSizeOptions = [10, 20, 50];

  displayedColumns = [
    'userId',
    'userName',
    'cuentaDestino',
    'referencia',
    'monto',
    'status',
    'createdAt',
    'actions',
  ];

  private destroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private spinner: SpinnerService,
    private vesTransactionsService: VesTransactionsService,
    private workingAccountService: WorkingAccountService,
    private dialog: MatDialog,
  ) {
    this.filterForm = this.fb.group({
      userId: [''],
      status: [''],
    });
  }

  ngOnInit(): void {
    this.loadInitialData();
  }

  async loadInitialData(): Promise<void> {
    await this.loadCuentasTrabajoVe();
    this.loadReferencias();
  }

  async loadCuentasTrabajoVe(): Promise<void> {
    try {
      this.cuentasTrabajoVe = (await this.workingAccountService.getWorkingAccounts(['VE']))
        .filter((c) => c.activo);
    } catch {
      this.cuentasTrabajoVe = [];
    }
  }

  findCuentaTrabajoMatch(ref: ReferenciaVesAdmin): CuentaTrabajo | null {
    if (!ref.cuenta || this.cuentasTrabajoVe.length === 0) return null;
    return this.cuentasTrabajoVe.find((ct) => ct.numeroCuenta === ref.cuenta) || null;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadReferencias(): void {
    this.loading = true;
    const filters = this.filterForm.value;

    this.vesTransactionsService
      .getReferenciasVesAdmin({
        page: this.page,
        limit: this.limit,
        userId: filters.userId || undefined,
        status: filters.status || undefined,
        onlyOperator: this.activeTab === 'operador',
        rail: this.activeRail === 'ALL' ? undefined : this.activeRail,
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          this.referencias = (res?.data?.items || []).map((ref: ReferenciaVesAdmin) => ({
            ...ref,
            cuentaTrabajoMatch: ref.status === 'PENDING' ? this.findCuentaTrabajoMatch(ref) : null,
          }));
          this.total = res?.data?.total || 0;
          this.totalPages = res?.data?.totalPages || 0;
          this.loading = false;
        },
        error: () => {
          this.referencias = [];
          this.loading = false;
        },
      });
  }

  applyFilters(): void {
    this.page = 1;
    this.loadReferencias();
  }

  clearFilters(): void {
    this.filterForm.reset({ userId: '', status: '' });
    this.page = 1;
    this.loadReferencias();
  }

  onPageChange(event: PageEvent): void {
    this.page = event.pageIndex + 1;
    this.limit = event.pageSize;
    this.loadReferencias();
  }

  toggleCard(id: string): void {
    this.expandedCardId = this.expandedCardId === id ? null : id;
  }

  getStatusLabel(status: string): string {
    const labels: { [key: string]: string } = {
      PENDING: 'Pendiente',
      APPROVED: 'Aprobada',
      REJECTED: 'Rechazada',
    };
    return labels[status?.toUpperCase()] || status || '';
  }

  getStatusBg(status: string): string {
    const colors: { [key: string]: string } = {
      PENDING: '#eab308',
      APPROVED: '#16a34a',
      REJECTED: '#dc2626',
    };
    return colors[status?.toUpperCase()] || '#6b7280';
  }

  openApproveModal(ref: ReferenciaVesAdmin): void {
    const isMobile = window.innerWidth < 768;
    const dialogRef = this.dialog.open(ApproveReferenciaModalComponent, {
      data: ref,
      maxWidth: isMobile ? '95vw' : '826px',
      minWidth: isMobile ? '90vw' : '500px',
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) this.loadReferencias();
    });
  }

  openRejectModal(ref: ReferenciaVesAdmin): void {
    const isMobile = window.innerWidth < 768;
    const dialogRef = this.dialog.open(RejectReferenciaModalComponent, {
      data: ref,
      maxWidth: isMobile ? '95vw' : '826px',
      minWidth: isMobile ? '90vw' : '500px',
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) this.loadReferencias();
    });
  }

  /**
   * Emparejado manual contra un movimiento de N58.
   *
   * Sólo se ofrece para N58: el backend responde 422 para Banco Plaza y R4,
   * que conservan su flujo original sin cambios.
   */
  esRailN58(ref: ReferenciaVesAdmin): boolean {
    return /n58/i.test(ref?.banco || '');
  }

  openEmparejarN58Modal(ref: ReferenciaVesAdmin): void {
    const isMobile = window.innerWidth < 768;
    const dialogRef = this.dialog.open(EmparejarN58ModalComponent, {
      data: ref,
      maxWidth: isMobile ? '95vw' : '826px',
      minWidth: isMobile ? '90vw' : '500px',
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) this.loadReferencias();
    });
  }

  openRegistrarOperadorModal(): void {
    const isMobile = window.innerWidth < 768;
    // Si el operador está filtrando por un rail específico (N58 o BP),
    // pre-seleccionamos ese banco en el modal y deshabilitamos el selector
    // (lockedRail). Si está en "Todos", el modal abre con selector libre
    // para que el operador elija.
    const lockedRail = this.activeRail === 'ALL' ? undefined : this.activeRail;

    const dialogRef = this.dialog.open(RegistrarReferenciaOperadorModalComponent, {
      data: { lockedRail },
      maxWidth: isMobile ? '95vw' : '826px',
      minWidth: isMobile ? '90vw' : '500px',
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) this.loadReferencias();
    });
  }

  setTab(tab: 'cliente' | 'operador'): void {
    if (this.activeTab === tab) return;
    this.activeTab = tab;
    this.page = 1;
    this.loadReferencias();
  }

  setRail(rail: 'ALL' | 'N58' | 'BANCO_PLAZA' | 'R4'): void {
    if (this.activeRail === rail) return;
    this.activeRail = rail;
    this.page = 1;
    this.loadReferencias();
  }
}
